import React, { useState, useEffect, useRef } from 'react';
import { getMySubordinates, getSubordinatesByManager, changeUserPassword, updateUserBlockStatus, getUsersGroupedByRole, reassignUser, getReassignmentJobsCheck, updateUserContactDetails } from '../../services/usersapi';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CheckCircle, 
  XCircle,
  Search,
  ChevronDown,
  ChevronUp,
  Users,
  Lock,
  Ban,
  ArrowRightCircle,
  Pen,
  X,
  MoreHorizontal,
  Eye,
  EyeOff,
  Users2
} from 'lucide-react';
import { Toaster, toast } from "react-hot-toast";
import { useRbac } from '../../context';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect, type SearchableSelectOption } from '../../components/ui/searchable-select';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { UnblockRequestsModal } from '../../components/UnblockRequestsModal';
import { getUnblockRequestsForReview } from '../../services/unblockRequestApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { registerPortalUser, getCurrentUser } from '../../services/apiclient';

// Define the shape of a subordinate
interface Subordinate {
  id: string;
  username: string;
  email: string;
  phone: string;
  role: string;
  location: string;
  isBlocked: boolean;
  currentManager: string | null;
  roleHistory: string[];
  createdAt: string;
}

// Define the shape of a manager for the dropdown
interface Manager {
  id: string;
  username: string;
}

// Define the shape of the API response
interface ApiResponse {
  status: string;
  results: number;
  data: {
    subordinates: Subordinate[];
  };
}

interface ManagersApiResponse {
  status: string;
  results: number;
  data: {
    [key: string]: Manager[];
  };
}

type SortField = keyof Subordinate;
type SortDirection = 'asc' | 'desc';

const isHmLikeRole = (r: string | undefined) =>
  r?.toUpperCase() === 'HM' || r?.toUpperCase() === 'AGENCY';

const MyHiringManagers: React.FC = () => {
  const { user, setUser } = useRbac();
  const [subordinates, setSubordinates] = useState<Subordinate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedSub, setSelectedSub] = useState<Subordinate | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newManagerId, setNewManagerId] = useState('');
  const [availableManagers, setAvailableManagers] = useState<Manager[]>([]);
  const [showMenu, setShowMenu] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const subordinatesPerPage = 10;
  const [showUnblockRequestsModal, setShowUnblockRequestsModal] = useState(false);
  const [pendingUnblockCount, setPendingUnblockCount] = useState(0);
  const [hideBlocked, setHideBlocked] = useState(true);
  const [expandedHms, setExpandedHms] = useState<Record<string, boolean>>({});
  const [hrByManager, setHrByManager] = useState<Record<string, Subordinate[]>>({});
  const [hrLoading, setHrLoading] = useState<Record<string, boolean>>({});
  const [migrateStep, setMigrateStep] = useState<1 | 2>(1); // 1: select manager, 2: confirm with common jobs
  const [commonJobs, setCommonJobs] = useState<{ id: string; jobId: string; title: string; location: string; status: string; companyName: string }[]>([]);
  const [jobsCheckLoading, setJobsCheckLoading] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ username: '', email: '', phone: '' });
  const [editUserErrors, setEditUserErrors] = useState({ username: '', email: '', phone: '' });
  const [editUserSaving, setEditUserSaving] = useState(false);
  type CreateUserRole = 'HR' | 'HM' | 'AGENCY' | 'SHM' | 'FBDE' | 'CONSULTANT' | 'FREELANCE_BDE' | 'BDE_INTERN';
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location: '',
    role: 'HM' as CreateUserRole,
    assignedManagerUserId: '',
  });
  const [createUserShowPassword, setCreateUserShowPassword] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserErrors, setCreateUserErrors] = useState<Record<string, string>>({});

  // Fetch pending unblock requests count
  const fetchPendingUnblockCount = async () => {
    if (user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN' || user?.role === 'ADMIN') {
      try {
        const response = await getUnblockRequestsForReview(1, 100, 'pending');
        if (response.success) {
          setPendingUnblockCount(response.data.requests.length);
        }
      } catch (error) {
        console.error('Failed to fetch pending unblock count:', error);
      }
    }
  };

  useEffect(() => {
    fetchPendingUnblockCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingUnblockCount, 30000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Refresh session so ADMIN-toggled canCreateUsers is visible without re-login
  useEffect(() => {
    if (user?.role !== 'HM') return;
    let cancelled = false;
    getCurrentUser()
      .then((res: any) => {
        const userData = res?.data?.user ?? res?.user;
        if (!cancelled && userData) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.role, setUser]);

  const fetchSubordinates = async (excludeBlocked = hideBlocked) => {
    try {
      const response = await getMySubordinates({ excludeBlocked });
      console.log('Subordinates Response:', response);
      if (response.status === 'success') {
        setSubordinates(response.data.subordinates);
      } else {
        setError('Failed to fetch subordinates');
      }
    } catch (err) {
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    console.log('newManagerId:', newManagerId);  
    console.log('availableManagers:', availableManagers);
  }, [newManagerId]);

  const fetchAvailableManagers = async (role: string) => {
    try {
      const response = await getUsersGroupedByRole();
      console.log('Managers Response:', response, user);
      if (response.status === 'success') {
        let managers: Manager[] = [];
        if (role === 'HR') {
          managers = response.data.HM || [];
        } else if (role === 'HM') {
          managers = [
            ...(response.data.SHM || []),
            ...(response.data.SHM_ADMIN || []),
            ...(response.data.AGENCY || []),
            ...(response.data.FBDE || []),
            ...(response.data.FBDE_ADMIN || []),
            ...(response.data.CONSULTANT || []),
          ];
        } else if (role === 'AGENCY') {
          managers = [
            ...(response.data.SHM || []),
            ...(response.data.SHM_ADMIN || []),
            ...(response.data.AGENCY_ADMIN || []),
            ...(response.data.FBDE_ADMIN || []),
            ...(response.data.FBDE || []),
            ...(response.data.CONSULTANT || []),
          ];
        } else if (role === 'SHM') {
          managers = response.data.SHM_ADMIN || [];
        } else if (role === 'FBDE') {
          managers = response.data.FBDE_ADMIN || [];
        } else if (role === 'AGENCY_ADMIN') {
          managers = response.data.ADMIN || [];
        } else if (role === 'SHM_ADMIN' || role === 'FBDE_ADMIN') {
          managers = response.data.ADMIN || [];
        } else {
          managers = response.data.ADMIN || [];
        }
        setAvailableManagers(managers);
      } else {
        toast.error('Failed to fetch available managers');
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
      toast.error('Error fetching managers');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and sort subordinates
  const filteredAndSortedSubordinates = React.useMemo(() => {
    let filtered = subordinates.filter(sub => {
      const matchesSearch = 
        sub.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.currentManager?.toLowerCase().includes(searchTerm.toLowerCase()) || false);
      
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null) return sortDirection === 'asc' ? -1 : 1;

      if (sortField === 'createdAt') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      } else if (sortField === 'isBlocked') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [subordinates, searchTerm, sortField, sortDirection]);

  const fetchHrForManager = async (managerId: string, excludeBlocked = hideBlocked) => {
    setHrLoading((prev) => ({ ...prev, [managerId]: true }));
    try {
      const response = await getSubordinatesByManager(managerId, { excludeBlocked });
      if (response?.status === 'success') {
        setHrByManager((prev) => ({ ...prev, [managerId]: response.data.subordinates || [] }));
      } else {
        toast.error('Failed to load HR list');
      }
    } catch (error: any) {
      console.error('Failed to fetch HRs for manager:', error);
      toast.error(error?.response?.data?.message || 'Failed to load HR list');
    } finally {
      setHrLoading((prev) => ({ ...prev, [managerId]: false }));
    }
  };

  useEffect(() => {
    setLoading(true);
    setHrByManager({});
    fetchSubordinates(hideBlocked);
    Object.entries(expandedHms).forEach(([id, expanded]) => {
      if (expanded) {
        fetchHrForManager(id, hideBlocked);
      }
    });
  }, [hideBlocked]);

  const toggleHmExpand = (hmId: string) => {
    setExpandedHms((prev) => {
      const next = !prev[hmId];
      // If expanding and no data loaded yet, fetch from backend
      if (next && !hrByManager[hmId] && !hrLoading[hmId]) {
        fetchHrForManager(hmId);
      }
      return { ...prev, [hmId]: next };
    });
  };

  // Pagination calculations
  const indexOfLastSubordinate = currentPage * subordinatesPerPage;
  const indexOfFirstSubordinate = indexOfLastSubordinate - subordinatesPerPage;
  const currentSubordinates = filteredAndSortedSubordinates.slice(indexOfFirstSubordinate, indexOfLastSubordinate);
  const totalPages = Math.ceil(filteredAndSortedSubordinates.length / subordinatesPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /** SHM_ADMIN / AGENCY_ADMIN / FBDE_ADMIN get the full hierarchy from the API; no nested expand needed. */
  const showSubtreeExpand =
    user?.role !== 'SHM_ADMIN' && user?.role !== 'AGENCY_ADMIN' && user?.role !== 'FBDE_ADMIN';

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-40" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-orange-600" /> : 
      <ChevronDown className="w-4 h-4 text-orange-600" />;
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!selectedSub) return;
    try {
      await changeUserPassword(selectedSub.id, newPassword, confirmPassword);
      toast.success('Password updated successfully!');
      // setShowChangePassword(false);
      // setShowMenu(null);
      // fetchSubordinates();
    } catch (err) {
      toast.error('Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character');
    } finally {
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleBlockToggle = async () => {
    if (!selectedSub) return;
    try {
      if (selectedSub.isBlocked) {
        await updateUserBlockStatus(selectedSub.id, false);
        toast.success('User unblocked successfully!');
      } else {
        await updateUserBlockStatus(selectedSub.id, true);
        toast.success('User blocked successfully!');
      }
      setShowBlockConfirm(false);
      setShowMenu(null);
      fetchSubordinates();
    } catch (err) {
      toast.error('Error updating user status');
    }
  };

  const handleProceedToConfirmation = async () => {
    if (!selectedSub || !newManagerId) {
      toast.error('Please select a valid manager');
      return;
    }
    setJobsCheckLoading(true);
    try {
      const response = await getReassignmentJobsCheck(selectedSub.id, newManagerId);
      if (response?.status === 'success' && response?.data) {
        setCommonJobs(response.data.commonJobs || []);
        setMigrateStep(2);
      }
    } catch (err) {
      // Error already handled in API
    } finally {
      setJobsCheckLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!selectedSub || !newManagerId) {
      toast.error('Please select a valid manager');
      return;
    }
    try {
      const response = await reassignUser(selectedSub.id, { newManagerUserId: newManagerId });
      console.log('Migrate Response:', response);
      toast.success('User migrated successfully!');
      setShowMigrate(false);
      setShowMenu(null);
      setMigrateStep(1);
      setCommonJobs([]);
      fetchSubordinates();
    } catch (err) {
      toast.error('Error migrating user');
    } finally {
      setNewManagerId('');
    }
  };

  const handleOpenMigrateModal = (sub: Subordinate) => {
    setSelectedSub(sub);
    setNewManagerId('');
    setMigrateStep(1);
    setCommonJobs([]);
    fetchAvailableManagers(sub.role);
    setShowMigrate(true);
  };

  const handleBackFromConfirmation = () => {
    setMigrateStep(1);
    setCommonJobs([]);
  };

  const handleOpenCreateUserModal = () => {
    setCreateUserErrors({});
    const managerId = (user as any)?.id ?? (user as any)?._id ?? '';
    const defaultRole: CreateUserRole =
      user?.role === 'AGENCY_ADMIN'
        ? 'AGENCY'
        : user?.role === 'FBDE_ADMIN'
          ? 'FBDE'
          : user?.role === 'HM'
            ? 'HR'
            : 'HM';
    setCreateUserForm({
      username: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      location: '',
      role: defaultRole,
      assignedManagerUserId: managerId,
    });
    setShowCreateUserModal(true);
  };

  const handleCreateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!createUserForm.username?.trim()) err.username = 'Username is required';
    if (!createUserForm.email?.trim()) err.email = 'Valid email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createUserForm.email)) err.email = 'Invalid email format';
    if (!/^\d{10}$/.test(createUserForm.phone?.trim() ?? '')) err.phone = 'Valid 10-digit phone is required';
    if (!createUserForm.password || createUserForm.password.length < 6) err.password = 'Password must be at least 6 characters';
    if (createUserForm.password !== createUserForm.confirmPassword) err.confirmPassword = 'Passwords do not match';
    if (!createUserForm.location?.trim()) err.location = 'Location is required';
    const managerId = (user as any)?.id ?? (user as any)?._id ?? '';
    const assignedManagerUserId =
      createUserForm.role === 'HM' ||
      createUserForm.role === 'AGENCY' ||
      (createUserForm.role === 'SHM' && user?.role === 'SHM_ADMIN') ||
      ((createUserForm.role === 'FBDE' || createUserForm.role === 'CONSULTANT') && user?.role === 'FBDE_ADMIN') ||
      (createUserForm.role === 'HR' && user?.role === 'HM')
        ? managerId
        : createUserForm.assignedManagerUserId;
    if (createUserForm.role === 'HR' && !assignedManagerUserId) err.assignedManagerUserId = 'Select a Hiring Manager';
    if (user?.role === 'HM' && createUserForm.role !== 'HR') {
      err.role = 'Hiring Managers can only create HR users';
    }
    setCreateUserErrors(err);
    if (Object.keys(err).length > 0) return;
    try {
      setCreateUserLoading(true);
      const payload = {
        username: createUserForm.username.trim(),
        email: createUserForm.email.trim(),
        phone: createUserForm.phone.trim(),
        password: createUserForm.password,
        location: createUserForm.location.trim(),
        role: createUserForm.role,
        assignedManagerUserId,
      };
      const response = await registerPortalUser(payload as any);
      if (response?.status === 'success') {
        toast.success(response.message || 'User created successfully');
        setShowCreateUserModal(false);
        fetchSubordinates();
      } else {
        toast.error((response as any)?.message || 'Failed to create user');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to create user';
      toast.error(msg);
    } finally {
      setCreateUserLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading hiring managers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-w-0 bg-gradient-to-br from-gray-50 to-orange-50 p-4 sm:p-6">
      {/* <Toaster position="top-right" /> */}
      <div className="max-w-7xl mx-auto min-w-0">
        {/* Header — stay stacked until lg so md/narrow main columns do not overflow */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 min-w-0 overflow-hidden">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center space-x-3">
              <div className="w-10 h-10 shrink-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 break-words">
                  {!isHmLikeRole(user.role) ? 'My Hiring Managers' : 'My Recruiters'}
                </h1>
                <p className="text-gray-600">{filteredAndSortedSubordinates.length} managers found</p>
              </div>
            </div>

            {/* Search Control and Actions */}
            <div className="flex min-w-0 w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:w-auto lg:max-w-none lg:justify-end">
              <label className="flex shrink-0 items-center gap-2 cursor-pointer select-none">
                <Switch
                  id="hide-blocked-users"
                  checked={hideBlocked}
                  onCheckedChange={(checked) => {
                    setHideBlocked(checked);
                    setCurrentPage(1);
                  }}
                />
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Hide blocked</span>
              </label>
              <div className="relative min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search managers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-w-0 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors sm:w-64"
                />
              </div>
              {((user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN' || user?.role === 'AGENCY' || user?.role === 'ADMIN' || user?.role === 'FBDE') ||
                (user?.role === 'HM' && user?.canCreateUsers)) && (
                <Button
                  onClick={handleOpenCreateUserModal}
                  className="shrink-0 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Create User
                </Button>
              )}
              {(user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN' || user?.role === 'ADMIN' || user?.role === 'FBDE') && (
                <Button
                  onClick={() => {
                    setShowUnblockRequestsModal(true);
                    fetchPendingUnblockCount(); // Refresh count when opening modal
                  }}
                  className="shrink-0 bg-white hover:bg-orange-50 text-orange-600 border-2 border-orange-600 relative"
                >
                  Unblock Requests
                  {pendingUnblockCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {pendingUnblockCount > 99 ? '99+' : pendingUnblockCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredAndSortedSubordinates.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No managers found</h3>
            <p className="text-gray-500">Try adjusting your search criteria.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('username')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Username</span>
                        <SortIcon field="username" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Email</span>
                        <SortIcon field="email" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('phone')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Phone</span>
                        <SortIcon field="phone" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('role')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Role</span>
                        <SortIcon field="role" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('isBlocked')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Status</span>
                        <SortIcon field="isBlocked" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Joined</span>
                        <SortIcon field="createdAt" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('location')}
                        className="flex items-center space-x-2 hover:text-orange-100 transition-colors"
                      >
                        <span>Location</span>
                        <SortIcon field="location" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentSubordinates.map((sub, index) => (
                    <React.Fragment key={sub.id}>
                    <tr className={`hover:bg-orange-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                              {sub.username}
                              {showSubtreeExpand && isHmLikeRole(sub.role) && (
                                <button
                                  type="button"
                                  className="text-orange-600 hover:text-orange-700 transition-colors"
                                  title="View HRs under this manager"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleHmExpand(sub.id);
                                  }}
                                >
                                  <Users2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-gray-900">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{sub.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-gray-900">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{sub.phone}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{sub.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${
                          sub.isBlocked 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {sub.isBlocked ? (
                            <XCircle className="w-3 h-3" />
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          <span>{sub.isBlocked ? 'Blocked' : 'Active'}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-gray-900">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>
                            {new Date(sub.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-gray-900">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{sub.location}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap relative">
                        <div className="flex items-center justify-end">
                          <button 
                            onClick={() => setShowMenu(showMenu === sub.id ? null : sub.id)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title="Actions"
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-600" />
                          </button>
                          {showMenu === sub.id && (
                            <div ref={menuRef} className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 py-2 border border-gray-100">
                              <button
                                onClick={() => {
                                  setSelectedSub(sub);
                                  setNewPassword('');
                                  setConfirmPassword('');
                                  setShowChangePassword(true);
                                }}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                              >
                                <Lock className="w-4 h-4 text-blue-600" />
                                <span>Change Password</span>
                              </button>
                              
                              <div className="h-[1px] bg-gray-100 my-2"></div>
                              
                              <button
                                onClick={() => {
                                  setSelectedSub(sub);
                                  setShowBlockConfirm(true);
                                }}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                              >
                                <Ban className="w-4 h-4 text-red-600" />
                                <span>{sub.isBlocked ? 'Unblock User' : 'Block User'}</span>
                              </button>
                              {(user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN') && (
                                <>
                                  <div className="h-[1px] bg-gray-100 my-2"></div>
                                  <button
                                    onClick={() => {
                                      setSelectedSub(sub);
                                      setEditUserForm({
                                        username: sub.username || '',
                                        email: sub.email || '',
                                        phone: sub.phone || '',
                                      });
                                      setEditUserErrors({ username: '', email: '', phone: '' });
                                      setShowEditUserModal(true);
                                      setShowMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                                  >
                                    <Pen className="w-4 h-4 text-orange-600" />
                                    <span>Edit User</span>
                                  </button>
                                </>
                              )}
                              
                              <div className="h-[1px] bg-gray-100 my-2"></div>
                              
                              <button
                                onClick={() => handleOpenMigrateModal(sub)}
                                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                              >
                                <ArrowRightCircle className="w-4 h-4 text-green-600" />
                                <span>Migrate User</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isHmLikeRole(sub.role) && expandedHms[sub.id] && (
                      <>
                        {hrLoading[sub.id] ? (
                          <tr className="bg-gray-50">
                            <td colSpan={9} className="px-6 py-2 text-xs text-gray-500">
                              Loading HRs...
                            </td>
                          </tr>
                        ) : !(hrByManager[sub.id]?.length) ? (
                          <tr className="bg-gray-50">
                            <td colSpan={9} className="px-6 py-2 text-xs text-gray-500">
                              No HRs assigned to this manager.
                            </td>
                          </tr>
                        ) : (
                          hrByManager[sub.id]!.map((hr, hrIdx) => (
                            <tr
                              key={hr.id}
                              className={`transition-colors ${hrIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-orange-50`}
                            >
                              <td className="px-6 py-2 pl-12">
                                <div className="flex items-center gap-2 text-sm">
                                  <div className="w-8 h-8 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-xs font-semibold">
                                    {hr.username?.[0]?.toUpperCase() || 'H'}
                                  </div>
                                  <div className="font-medium text-gray-900">{hr.username}</div>
                                </div>
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-800">{hr.email}</td>
                              <td className="px-6 py-2 text-sm text-gray-800">{hr.phone}</td>
                              <td className="px-6 py-2 text-sm text-gray-800">{hr.role}</td>
                              <td className="px-6 py-2">
                                <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${hr.isBlocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {hr.isBlocked ? 'Blocked' : 'Active'}
                                </span>
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-800">
                                {new Date(hr.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-800">
                                <div className="flex items-center space-x-1">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span>{hr.location}</span>
                                </div>
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap relative">
                                <div className="flex items-center justify-end">
                                  <button
                                    onClick={() => setShowMenu(showMenu === `hr-${hr.id}` ? null : `hr-${hr.id}`)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    title="Actions"
                                  >
                                    <MoreHorizontal className="w-4 h-4 text-gray-600" />
                                  </button>
                                  {showMenu === `hr-${hr.id}` && (
                                    <div ref={menuRef} className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 py-2 border border-gray-100">
                                      <button
                                        onClick={() => {
                                          setSelectedSub(hr);
                                          setNewPassword('');
                                          setConfirmPassword('');
                                          setShowChangePassword(true);
                                          setShowMenu(null);
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                                      >
                                        <Lock className="w-4 h-4 text-blue-600" />
                                        <span>Change Password</span>
                                      </button>
                                      <div className="h-[1px] bg-gray-100 my-2" />
                                      <button
                                        onClick={() => {
                                          setSelectedSub(hr);
                                          setShowBlockConfirm(true);
                                          setShowMenu(null);
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                                      >
                                        <Ban className="w-4 h-4 text-red-600" />
                                        <span>{hr.isBlocked ? 'Unblock User' : 'Block User'}</span>
                                      </button>
                                      {(user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN') && (
                                        <>
                                          <div className="h-[1px] bg-gray-100 my-2" />
                                          <button
                                            onClick={() => {
                                              setSelectedSub(hr);
                                              setEditUserForm({
                                                username: hr.username || '',
                                                email: hr.email || '',
                                                phone: hr.phone || '',
                                              });
                                              setEditUserErrors({ username: '', email: '', phone: '' });
                                              setShowEditUserModal(true);
                                              setShowMenu(null);
                                            }}
                                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                                          >
                                            <Pen className="w-4 h-4 text-orange-600" />
                                            <span>Edit User</span>
                                          </button>
                                        </>
                                      )}
                                      <div className="h-[1px] bg-gray-100 my-2" />
                                      <button
                                        onClick={() => {
                                          handleOpenMigrateModal(hr);
                                          setShowMenu(null);
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-3 transition-colors"
                                      >
                                        <ArrowRightCircle className="w-4 h-4 text-green-600" />
                                        <span>Migrate User</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                &lt;
              </Button>
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                if (totalPages <= 7) {
                  return (
                    <Button
                      key={pageNum}
                      variant="outline"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded ${currentPage === pageNum ? 'bg-orange-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      {pageNum}
                    </Button>
                  );
                } else {
                  if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)) {
                    return (
                      <Button
                        key={pageNum}
                        variant="outline"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded ${currentPage === pageNum ? 'bg-orange-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                    return (
                      <span key={pageNum} className="px-3 py-1 text-gray-500">...</span>
                    );
                  }
                  return null;
                }
              })}
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                &gt;
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* Change Password Modal */}
      {showChangePassword && selectedSub && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Change Password for <span className="text-orange-600">{selectedSub.username}</span></h3>
              <button
                onClick={() => {
                  setShowChangePassword(false);
                  setShowMenu(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 "
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 "
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowChangePassword(false);
                    setShowMenu(null);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg shadow-md"
                >
                  Save Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Confirmation Modal */}
      {showBlockConfirm && selectedSub && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedSub.isBlocked ? 'Unblock' : 'Block'} <span className="text-orange-600">{selectedSub.username}</span>?
              </h3>
              <button
                onClick={() => {
                  setShowBlockConfirm(false);
                  setShowMenu(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                Are you sure you want to {selectedSub.isBlocked ? 'unblock' : 'block'} this user?
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowBlockConfirm(false);
                    setShowMenu(null);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlockToggle}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg shadow-md"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal (SHM only action) */}
      {showEditUserModal && selectedSub && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit User <span className="text-orange-600">{selectedSub.username}</span>
              </h3>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditUserErrors({ username: '', email: '', phone: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="space-y-1">
                <Label htmlFor="mhm-edit-username">Username *</Label>
                <Input
                  id="mhm-edit-username"
                  value={editUserForm.username}
                  onChange={(e) => {
                    setEditUserForm((p) => ({ ...p, username: e.target.value }));
                    setEditUserErrors((p) => ({ ...p, username: '' }));
                  }}
                />
                {editUserErrors.username && <p className="text-xs text-red-600">{editUserErrors.username}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="mhm-edit-email">Email *</Label>
                <Input
                  id="mhm-edit-email"
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => {
                    setEditUserForm((p) => ({ ...p, email: e.target.value }));
                    setEditUserErrors((p) => ({ ...p, email: '' }));
                  }}
                />
                {editUserErrors.email && <p className="text-xs text-red-600">{editUserErrors.email}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="mhm-edit-phone">Phone *</Label>
                <Input
                  id="mhm-edit-phone"
                  inputMode="numeric"
                  maxLength={10}
                  value={editUserForm.phone}
                  onChange={(e) => {
                    setEditUserForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }));
                    setEditUserErrors((p) => ({ ...p, phone: '' }));
                  }}
                />
                {editUserErrors.phone && <p className="text-xs text-red-600">{editUserErrors.phone}</p>}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowEditUserModal(false);
                    setEditUserErrors({ username: '', email: '', phone: '' });
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  disabled={editUserSaving}
                  onClick={async () => {
                    const payload = {
                      username: editUserForm.username.trim(),
                      email: editUserForm.email.trim(),
                      phone: editUserForm.phone.trim(),
                    };
                    const errs = {
                      username: payload.username ? '' : 'Username is required.',
                      email: payload.email ? '' : 'Email is required.',
                      phone: payload.phone ? '' : 'Phone is required.',
                    };
                    setEditUserErrors(errs);
                    if (errs.username || errs.email || errs.phone) return;
                    setEditUserSaving(true);
                    try {
                      await updateUserContactDetails(selectedSub.id, payload);
                      setSubordinates((prev) =>
                        prev.map((u) => (u.id === selectedSub.id ? { ...u, ...payload } : u))
                      );
                      setShowEditUserModal(false);
                      toast.success('User updated successfully.');
                    } catch (error: any) {
                      toast.error(error?.response?.data?.message || 'Failed to update user.');
                    } finally {
                      setEditUserSaving(false);
                    }
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg shadow-md disabled:opacity-60"
                >
                  {editUserSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Migrate Modal */}
      {showMigrate && selectedSub && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Migrate <span className="text-orange-600">{selectedSub.username}</span>
                {migrateStep === 2 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    → {availableManagers.find((m: any) => m._id === newManagerId)?.username || 'New Manager'}
                  </span>
                )}
              </h3>
              <button
                onClick={() => {
                  setShowMigrate(false);
                  setShowMenu(null);
                  setAvailableManagers([]);
                  setNewManagerId('');
                  setMigrateStep(1);
                  setCommonJobs([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {migrateStep === 1 ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select new manager</label>
                    <SearchableSelect
                      options={availableManagers.map((manager: any) => ({
                        value: manager._id,
                        label: manager.username,
                        searchText: [manager.username, manager.email].filter(Boolean).join(' ')
                      }))}
                      value={newManagerId}
                      onValueChange={setNewManagerId}
                      placeholder={`Select ${selectedSub.role === 'HM' ? 'SHM / Agency' : selectedSub.role === 'AGENCY' ? 'AGENCY_ADMIN / SHM' : selectedSub.role === 'HR' ? 'HM' : 'Admin'}`}
                      searchPlaceholder="Search by name or email..."
                      emptyText={`No ${selectedSub.role === 'HM' ? 'SHMs or agency users' : selectedSub.role === 'AGENCY' ? 'agency managers' : selectedSub.role === 'HR' ? 'HMs' : 'Admins'} found`}
                      searchable
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500"
                    />
                    {availableManagers.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">No {selectedSub.role === 'HM' ? 'SHMs or agency users' : selectedSub.role === 'AGENCY' ? 'agency managers' : selectedSub.role === 'HR' ? 'HMs' : 'Admins'} available</p>
                    )}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowMigrate(false);
                        setShowMenu(null);
                        setAvailableManagers([]);
                        setNewManagerId('');
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceedToConfirmation}
                      disabled={!newManagerId || availableManagers.length === 0 || jobsCheckLoading}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {jobsCheckLoading ? 'Checking...' : 'Proceed'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Common job assignments</p>
                    <p className="text-xs text-gray-500 mb-3">
                      Only jobs that exist in both the transferring account and the receiving manager&apos;s account are shown below. These will remain accessible after migration.
                    </p>
                    {commonJobs.length === 0 ? (
                      <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
                        No common job assignments. The user may lose access to some jobs after migration.
                      </p>
                    ) : (
                      <ul className="space-y-2 max-h-48 overflow-y-auto">
                        {commonJobs.map((job) => (
                          <li key={job.id} className="flex items-start gap-2 text-sm text-gray-800 bg-white rounded px-3 py-2 border border-gray-100">
                            <span className="font-medium truncate">{job.title}</span>
                            <span className="text-gray-500 text-xs whitespace-nowrap">{job.companyName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleBackFromConfirmation}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleMigrate}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg shadow-md"
                    >
                      Confirm Reassignment
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal (SHM/SHM_ADMIN/FBDE: HR/HM only; AGENCY_ADMIN: Agency; AGENCY: HM/HR) */}
      <Dialog open={showCreateUserModal} onOpenChange={(open) => { if (!open) setShowCreateUserModal(false); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Create User</DialogTitle>
            <DialogDescription>
              {user?.role === 'SHM_ADMIN'
                ? 'Create an HR, Hiring Manager, or SHM user under your account.'
                : user?.role === 'AGENCY_ADMIN'
                  ? 'Create an Agency user under your account.'
                  : user?.role === 'FBDE_ADMIN'
                    ? 'Create an HR, Hiring Manager, or Franchise (FBDE) user under your account.'
                  : user?.role === 'AGENCY'
                    ? 'Create a Hiring Manager or HR user under your account.'
                  : user?.role === 'SHM'
                    ? 'Create an HR or Hiring Manager user under your account.'
                  : user?.role === 'FBDE'
                    ? 'Create an HR or Hiring Manager user under your account.'
                  : user?.role === 'HM'
                    ? 'Create an HR user under your account.'
                  : 'Create an HR, Hiring Manager, or Agency user under your account.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUserSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Username</Label>
                <Input
                  value={createUserForm.username}
                  onChange={(e) => setCreateUserForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder="Username"
                  className="border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                />
                {createUserErrors.username && <p className="text-red-500 text-xs">{createUserErrors.username}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input
                  type="email"
                  value={createUserForm.email}
                  onChange={(e) => setCreateUserForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                />
                {createUserErrors.email && <p className="text-red-500 text-xs">{createUserErrors.email}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Phone (10 digits)</Label>
                <Input
                  value={createUserForm.phone}
                  onChange={(e) => setCreateUserForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="9876543210"
                  maxLength={10}
                  className="border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                />
                {createUserErrors.phone && <p className="text-red-500 text-xs">{createUserErrors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Location</Label>
                <Input
                  value={createUserForm.location}
                  onChange={(e) => setCreateUserForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="City or location"
                  className="border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                />
                {createUserErrors.location && <p className="text-red-500 text-xs">{createUserErrors.location}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Role</Label>
              <Select
                value={createUserForm.role}
                onValueChange={(v: CreateUserRole) => {
                  const managerId = (user as any)?.id ?? (user as any)?._id ?? '';
                  setCreateUserForm((f) => {
                    let nextManager = f.assignedManagerUserId;
                    if (
                      v === 'HM' ||
                      v === 'AGENCY' ||
                      (v === 'SHM' && user?.role === 'SHM_ADMIN') ||
                      ((v === 'FBDE' || v === 'CONSULTANT') && user?.role === 'FBDE_ADMIN') ||
                      (v === 'HR' && user?.role === 'HM')
                    ) {
                      nextManager = managerId;
                    } else if (v === 'HR') {
                      nextManager = '';
                    }
                    return { ...f, role: v, assignedManagerUserId: nextManager };
                  });
                }}
              >
                <SelectTrigger className="border-gray-300 focus:ring-orange-500">
                  <SelectValue />
                </SelectTrigger>
                {user?.role === 'FBDE' && (
                  <SelectContent>
                    {/* <SelectItem value="BDE_INTERN">BDE Intern</SelectItem>
                    <SelectItem value="FREELANCE_BDE">Freelance BDE</SelectItem> */}
                    <SelectItem value="HM">Hiring Manager (HM)</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'SHM' && (
                  <SelectContent>
                    <SelectItem value="HM">Hiring Manager (HM)</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'SHM_ADMIN' && (
                  <SelectContent>
                    <SelectItem value="HM">Hiring Manager (HM)</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="SHM">Senior Hiring Manager (SHM)</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'AGENCY_ADMIN' && (
                  <SelectContent>
                    <SelectItem value="AGENCY">Agency</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'FBDE_ADMIN' && (
                  <SelectContent>
                    <SelectItem value="HM">Hiring Manager (HM)</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="FBDE">Franchise (FBDE)</SelectItem>
                    <SelectItem value="CONSULTANT">Consultant</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'AGENCY' && (
                  <SelectContent>
                    <SelectItem value="HM">Hiring Manager (HM)</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                )}

                {user?.role === 'HM' && (
                  <SelectContent>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                )}
              </Select>
            </div>
            {createUserForm.role === 'HR' && user?.role !== 'HM' && (
              <div className="space-y-2">
                <Label className="text-gray-700">Assigned Hiring Manager</Label>
                <Select
                  value={createUserForm.assignedManagerUserId}
                  onValueChange={(v) => setCreateUserForm((f) => ({ ...f, assignedManagerUserId: v }))}
                >
                  <SelectTrigger className="border-gray-300 focus:ring-orange-500">
                    <SelectValue placeholder="Select HM" />
                  </SelectTrigger>
                  <SelectContent>
                    {subordinates.filter((s) => s.role?.toUpperCase() === 'HM').map((hm) => (
                      <SelectItem key={hm.id} value={hm.id}>{hm.username} ({hm.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createUserErrors.assignedManagerUserId && <p className="text-red-500 text-xs">{createUserErrors.assignedManagerUserId}</p>}
              </div>
            )}
            {(createUserForm.role === 'HM' ||
              createUserForm.role === 'AGENCY' ||
              (createUserForm.role === 'SHM' && user?.role === 'SHM_ADMIN') ||
              ((createUserForm.role === 'FBDE' || createUserForm.role === 'CONSULTANT') && user?.role === 'FBDE_ADMIN') ||
              (createUserForm.role === 'HR' && user?.role === 'HM')) && (
              <div className="space-y-1">
                <Label className="text-gray-700">Assigned Manager</Label>
                <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">You (current user)</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Password</Label>
                <div className="relative">
                  <Input
                    type={createUserShowPassword ? 'text' : 'password'}
                    value={createUserForm.password}
                    onChange={(e) => setCreateUserForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Min 6 characters"
                    className="border-gray-300 focus:ring-orange-500 focus:border-orange-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setCreateUserShowPassword(!createUserShowPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {createUserShowPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {createUserErrors.password && <p className="text-red-500 text-xs">{createUserErrors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Confirm Password</Label>
                <Input
                  type="password"
                  value={createUserForm.confirmPassword}
                  onChange={(e) => setCreateUserForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                  placeholder="Confirm password"
                  className="border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                />
                {createUserErrors.confirmPassword && <p className="text-red-500 text-xs">{createUserErrors.confirmPassword}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateUserModal(false)} disabled={createUserLoading}>
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" disabled={createUserLoading}>
                {createUserLoading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unblock Requests Modal */}
      <UnblockRequestsModal
        isOpen={showUnblockRequestsModal}
        onClose={() => setShowUnblockRequestsModal(false)}
        onRequestUpdated={fetchPendingUnblockCount}
      />
    </div>
  );
};

export default MyHiringManagers;